import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  MessageSquare,
  ArrowLeft,
  ArrowUp,
  Search,
  Info
} from 'lucide-react';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useApiClient } from '../hooks/useApiClient';
import { useToast } from '../hooks/useToast';
import { Toast, ConfirmDialog } from '../components/Toast';
import { RichTextEditor } from '../components/common/RichTextEditor';

// Per-manager active question limits. Must match the backend constants in
// FeedbackQuestionService (MinActiveQuestionsPerManager / MaxActiveQuestionsPerManager).
const MIN_ACTIVE_QUESTIONS = 5;
const MAX_ACTIVE_QUESTIONS = 7;

interface FeedbackQuestion {
  id: number;
  question: string;
  placeholder: string | null;
  displayOrder: number;
  managerEmployeeId: string | null;
  isActive: boolean;
}

interface QuestionFormData {
  question: string;
  placeholder: string;
  displayOrder: number;
  isActive: boolean;
}

export function ManageQuestionsPage() {
  const navigate = useNavigate();
  const { userProfile, loading: profileLoading } = useUserProfileContext();
  const { apiCall } = useApiClient();
  const { toasts, hideToast, success: showSuccess, error: showError } = useToast();

  const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Add/Edit modal state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>({
    question: '',
    placeholder: '',
    displayOrder: 1,
    isActive: true
  });

  // Delete confirmation state
  const [deleteCandidate, setDeleteCandidate] = useState<FeedbackQuestion | null>(null);

  const hasDirectReports = useMemo(
    () => (userProfile?.directReports?.length || 0) > 0,
    [userProfile?.directReports?.length]
  );

  // Back-to-top scroll listener
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Redirect non-managers
  useEffect(() => {
    if (!profileLoading && userProfile && !hasDirectReports) {
      navigate('/dashboard');
    }
  }, [profileLoading, userProfile, hasDirectReports, navigate]);

  // Fetch questions (also triggers backend copy-on-write so the manager owns their list)
  const fetchQuestions = useCallback(async () => {
    if (!userProfile?.employeeId) return;
    setLoading(true);
    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions/manager/${encodeURIComponent(userProfile.employeeId)}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          setQuestions(result.data);
        }
      } else {
        showError('Failed to load questions');
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      showError('Failed to load questions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.employeeId]);

  useEffect(() => {
    if (userProfile?.employeeId && hasDirectReports) {
      fetchQuestions();
    }
  }, [userProfile?.employeeId, hasDirectReports, fetchQuestions]);

  // Derived state
  const activeCount = useMemo(() => questions.filter((q) => q.isActive).length, [questions]);
  const atMax = activeCount >= MAX_ACTIVE_QUESTIONS;
  const atMin = activeCount <= MIN_ACTIVE_QUESTIONS;

  const filteredQuestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...questions].sort((a, b) => {
      // Active first, then by display order
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.displayOrder - b.displayOrder;
    });
    if (!q) return sorted;
    return sorted.filter(
      (x) =>
        x.question.toLowerCase().includes(q) ||
        (x.placeholder ?? '').toLowerCase().includes(q)
    );
  }, [questions, search]);

  // ---------- Handlers ----------

  const openAddForm = () => {
    if (atMax) {
      showError(
        `You already have ${MAX_ACTIVE_QUESTIONS} active questions. Turn one off before adding a new one.`
      );
      return;
    }
    setFormData({
      question: '',
      placeholder: '',
      displayOrder: questions.length + 1,
      isActive: true
    });
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (q: FeedbackQuestion) => {
    setFormData({
      question: q.question,
      placeholder: q.placeholder || '',
      displayOrder: q.displayOrder,
      isActive: q.isActive
    });
    setEditingId(q.id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.question.trim()) {
      showError('Question text is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        question: formData.question.trim(),
        placeholder: formData.placeholder.trim() || null,
        displayOrder: formData.displayOrder,
        managerEmployeeId: userProfile?.employeeId,
        isActive: formData.isActive
      };

      const url = editingId
        ? `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions/${editingId}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions`;

      const response = await apiCall(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        closeForm();
        await fetchQuestions();
        showSuccess(editingId ? 'Question updated' : 'Question added');
      } else {
        let message = 'Failed to save question';
        try {
          const result = await response.json();
          if (result?.message) message = result.message;
        } catch {
          /* no JSON body */
        }
        showError(message);
      }
    } catch (err) {
      console.error('Error saving question:', err);
      showError('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;
    const target = deleteCandidate;
    setDeleteCandidate(null);

    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions/${target.id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        await fetchQuestions();
        showSuccess('Question deleted');
      } else {
        let message = 'Failed to delete question';
        try {
          const result = await response.json();
          if (result?.message) message = result.message;
        } catch {
          /* no JSON body */
        }
        showError(message);
      }
    } catch (err) {
      console.error('Error deleting question:', err);
      showError('An error occurred while deleting');
    }
  };

  // Inline checkbox: flip IsActive in-place. Backend enforces min 5 / max 7
  // and returns a friendly message if the toggle would violate the limit.
  const handleToggleActive = async (q: FeedbackQuestion) => {
    if (togglingId !== null) return; // prevent rapid double-clicks

    // Client-side guard for instant feedback (backend still re-validates).
    if (q.isActive && atMin) {
      showError(`You must keep at least ${MIN_ACTIVE_QUESTIONS} active questions.`);
      return;
    }
    if (!q.isActive && atMax) {
      showError(
        `You already have ${MAX_ACTIVE_QUESTIONS} active questions. Turn one off before turning this on.`
      );
      return;
    }

    setTogglingId(q.id);
    try {
      const payload = {
        question: q.question,
        placeholder: q.placeholder,
        displayOrder: q.displayOrder,
        managerEmployeeId: userProfile?.employeeId,
        isActive: !q.isActive
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions/${q.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        await fetchQuestions();
      } else {
        let message = 'Failed to update question';
        try {
          const result = await response.json();
          if (result?.message) message = result.message;
        } catch {
          /* no JSON body */
        }
        showError(message);
      }
    } catch (err) {
      console.error('Error toggling question:', err);
      showError('An error occurred while updating');
    } finally {
      setTogglingId(null);
    }
  };

  // ---------- Render ----------

  if (profileLoading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-pink-500 animate-spin" />
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasDirectReports) {
    return null;
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 shadow-xl text-white">
            <div className="flex items-center gap-4 mb-2">
              <MessageSquare className="w-10 h-10" />
              <h1 className="text-3xl font-bold">Feedback Questions</h1>
            </div>
            <p className="text-white/80 ml-14">
              Pick the questions you want on your team's next connect ({MIN_ACTIVE_QUESTIONS}&ndash;{MAX_ACTIVE_QUESTIONS} active).
            </p>
          </div>
        </div>

        {/* Stats + Controls bar */}
        <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search questions..."
                className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all placeholder:text-slate-400"
              />
            </div>

            {/* Stat pill + Add button */}
            <div className="flex items-center justify-end gap-3">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Active
                </span>
                <span
                  className={`text-sm font-bold ${
                    atMax
                      ? 'text-amber-600'
                      : atMin
                      ? 'text-pink-600'
                      : 'text-slate-800'
                  }`}
                >
                  {activeCount}
                </span>
                <span className="text-xs text-slate-400">/ {MAX_ACTIVE_QUESTIONS}</span>
              </div>

              <button
                onClick={openAddForm}
                disabled={atMax}
                title={
                  atMax
                    ? `You already have ${MAX_ACTIVE_QUESTIONS} active questions. Turn one off before adding a new one.`
                    : 'Add a new question'
                }
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                  atMax
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-sm shadow-pink-500/20'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>
          </div>
        </div>

        {/* Questions Table */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-4 bg-slate-100 rounded-2xl inline-block mb-3">
                <MessageSquare className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">
                {search.trim()
                  ? 'No questions match your search'
                  : 'No questions yet \u2014 click "Add Question" to create one.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 w-20">
                      On
                    </th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 w-16">
                      #
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                      Question
                    </th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6 w-40">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredQuestions.map((q, index) => {
                    const tickWouldExceedMax = !q.isActive && atMax;
                    const untickWouldGoBelowMin = q.isActive && atMin;
                    const toggleBlocked = tickWouldExceedMax || untickWouldGoBelowMin;
                    const toggleBusy = togglingId === q.id;

                    let toggleTitle = q.isActive
                      ? 'Turn off (remove from next connect)'
                      : 'Turn on (include in next connect)';
                    if (tickWouldExceedMax) {
                      toggleTitle = `You already have ${MAX_ACTIVE_QUESTIONS} active questions. Turn one off first.`;
                    } else if (untickWouldGoBelowMin) {
                      toggleTitle = `You must keep at least ${MIN_ACTIVE_QUESTIONS} active questions.`;
                    }

                    return (
                      <tr
                        key={q.id}
                        className={`hover:bg-slate-50/50 transition-colors ${
                          !q.isActive ? 'bg-slate-50/30' : ''
                        }`}
                      >
                        {/* Toggle */}
                        <td className="py-4 px-4">
                          <div className="flex justify-center">
                            <button
                              type="button"
                              onClick={() => {
                                if (toggleBlocked || toggleBusy) return;
                                handleToggleActive(q);
                              }}
                              disabled={toggleBlocked || toggleBusy}
                              title={toggleTitle}
                              aria-pressed={q.isActive}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                                q.isActive ? 'bg-emerald-500' : 'bg-slate-300'
                              } ${
                                toggleBlocked || toggleBusy
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'cursor-pointer'
                              }`}
                            >
                              <span
                                className={`inline-flex items-center justify-center h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                  q.isActive ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              >
                                {toggleBusy && (
                                  <Loader2 className="w-3 h-3 text-slate-500 animate-spin" />
                                )}
                              </span>
                            </button>
                          </div>
                        </td>

                        {/* Index */}
                        <td className="py-4 px-4 text-center">
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                              q.isActive
                                ? 'bg-pink-50 text-pink-600'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {index + 1}
                          </span>
                        </td>

                        {/* Question + placeholder */}
                        <td className="py-4 px-6">
                          <p
                            className={`font-medium mb-1 ${
                              q.isActive ? 'text-slate-800' : 'text-slate-500'
                            }`}
                          >
                            {q.question}
                          </p>
                          {q.placeholder && (
                            <p className="text-xs text-slate-500 italic line-clamp-2">
                              {q.placeholder}
                            </p>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => openEditForm(q)}
                              className="p-2 text-slate-500 hover:text-pink-600 hover:bg-pink-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteCandidate(q)}
                              className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info note */}
        <div className="mt-6 p-4 bg-blue-50/60 border border-blue-200 rounded-2xl flex gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900">
            Editing, turning off, or deleting questions only affects connects you start{' '}
            <strong>from now on</strong>. Connects that are already in progress or completed keep the
            exact questions they were created with.
          </p>
        </div>
      </main>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">
                {editingId ? 'Edit Question' : 'Add New Question'}
              </h2>
              <button
                onClick={closeForm}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Question Text <span className="text-pink-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.question}
                    onChange={(e) =>
                      setFormData({ ...formData, question: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
                    placeholder="Enter the feedback question..."
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Placeholder / Hint
                  </label>
                  <RichTextEditor
                    value={formData.placeholder}
                    onChange={(updatedValue) =>
                      setFormData({ ...formData, placeholder: updatedValue })
                    }
                    placeholder="Hint text shown inside the answer box..."
                    minHeightClassName="min-h-[90px]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.displayOrder}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        displayOrder: parseInt(e.target.value, 10) || 1
                      })
                    }
                    className="w-32 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Lower numbers appear first on the connect form.
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div>
                    <label className="text-sm font-semibold text-slate-700">
                      Include in next connect
                    </label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formData.isActive
                        ? 'This question will appear on the next connect you start.'
                        : 'This question is saved but hidden from new connects.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, isActive: !formData.isActive })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      formData.isActive ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                        formData.isActive ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/50">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl font-medium text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteCandidate && (
        <ConfirmDialog
          title="Delete question?"
          message={`"${deleteCandidate.question}" will be removed from your list. Past connects that already used it are not affected.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}

      {/* Toasts */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      {/* Back to Top FAB */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40 flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          showScrollTop
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  );
}
